import { useState, useEffect, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, Zap, ArrowRight } from 'lucide-react'
import { publicApi, PricingPlan } from '../../services/api'
import { AnimatedSection } from '../animations/AnimatedSection'

declare global {
  interface Window {
    Paddle: any
  }
}

interface SimplifiedPricingTier {
  name: string
  price: string
  description: string
  topFeatures: string[]
  popular?: boolean
  priceId: string
  loading?: boolean
  monthlyPrice?: number
}

export const PricingPreviewSection = memo(function PricingPreviewSection() {
  const navigate = useNavigate()
  const [pricingTiers, setPricingTiers] = useState<SimplifiedPricingTier[]>([])
  const [loading, setLoading] = useState(true)
  const [paddleInitialized, setPaddleInitialized] = useState(false)

  // Fetch pricing plans from API on mount
  useEffect(() => {
    const fetchPricingPlans = async () => {
      setLoading(true)
      try {
        const plans = await publicApi.getPricingPlans()
        
        // Convert to simplified format, take only top 3-4 features, then sort by price
        const tiers = plans
          .map((plan: PricingPlan) => ({
            name: plan.planName,
            price: plan.monthlyPrice === 0 ? '$0' : `${plan.monthlyPrice}`,
            description: plan.description,
            topFeatures: plan.features.slice(0, 4), // Show only 3-4 key features
            popular: plan.isPopular,
            priceId: plan.paddlePriceId,
            loading: plan.paddlePriceId ? true : false, // Mark paid tiers for Paddle price fetching
            monthlyPrice: plan.monthlyPrice, // Keep for sorting
          }))
          .sort((a, b) => a.monthlyPrice! - b.monthlyPrice!) // Sort by price ascending
        
        setPricingTiers(tiers)
      } catch (error) {
        console.error('Error fetching pricing plans:', error)
        // Fallback to default tiers (sorted by price)
        setPricingTiers([
          {
            name: 'Builder',
            price: '$5',
            description: 'Perfect for getting started',
            topFeatures: [
              '100 credits per month',
              'Access to all AI models',
              'API key management',
              'Email support'
            ],
            popular: false,
            priceId: '',
            loading: true,
            monthlyPrice: 5,
          },
          {
            name: 'Launch',
            price: '$29',
            description: 'For professionals and growing teams',
            topFeatures: [
              '1,000 credits per month',
              'Access to all AI models',
              'API key management',
              'Usage analytics dashboard'
            ],
            popular: true,
            priceId: '',
            loading: true,
            monthlyPrice: 29,
          },
          {
            name: 'Growth',
            price: '$99',
            description: 'For scaling applications',
            topFeatures: [
              '5,000 credits per month',
              'Access to all AI models',
              'API key management',
              'Usage analytics dashboard'
            ],
            popular: false,
            priceId: '',
            loading: true,
            monthlyPrice: 99,
          },
          {
            name: 'Scale',
            price: '$299',
            description: 'For enterprise-scale deployments',
            topFeatures: [
              '20,000 credits per month',
              'Access to all AI models',
              'API key management',
              'Usage analytics dashboard'
            ],
            popular: false,
            priceId: '',
            loading: true,
            monthlyPrice: 299,
          }
        ])
      } finally {
        setLoading(false)
      }
    }

    fetchPricingPlans()
  }, [])

  // Initialize Paddle and fetch real-time prices
  useEffect(() => {
    // Check if Paddle script is already loaded
    if (window.Paddle) {
      setPaddleInitialized(true)
      return
    }

    const script = document.createElement('script')
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js'
    script.async = true
    document.body.appendChild(script)

    script.onload = () => {
      if (window.Paddle) {
        const environment = import.meta.env.VITE_PADDLE_ENVIRONMENT || 'sandbox'
        const token = import.meta.env.VITE_PADDLE_CLIENT_TOKEN || ''
        
        window.Paddle.Environment.set(environment)
        window.Paddle.Initialize({
          token: token,
          eventCallback: (_event: any) => {
          },
        })

        setPaddleInitialized(true)
      }
    }

    return () => {
      // Don't remove script as it might be used by other components
    }
  }, [])

  // Fetch Paddle prices after both pricing plans and Paddle are loaded
  useEffect(() => {
    if (!paddleInitialized || loading || pricingTiers.length === 0) return

    const fetchPaddlePrices = async () => {
      if (!window.Paddle) return

      try {
        // Fetch real-time prices from Paddle for each tier with a priceId
        pricingTiers.forEach((tier) => {
          if (tier.priceId && tier.loading) {
            window.Paddle.PricePreview({
              items: [{ priceId: tier.priceId, quantity: 1 }],
            }).then((result: any) => {
              if (result?.data?.details?.lineItems?.[0]) {
                const rawPrice = result.data.details.lineItems[0].formattedTotals.total
                // Remove decimals from price (e.g., "$29.00" -> "$29")
                const price = rawPrice.replace(/\.00$/, '').replace(/\.\d{2}$/, (match: string) => {
                  // Only keep decimals if they're not .00
                  return match === '.00' ? '' : match
                })
                setPricingTiers((prev) =>
                  prev.map((t) =>
                    t.name === tier.name
                      ? { ...t, price: price, loading: false }
                      : t
                  )
                )
              }
            }).catch((error: any) => {
              console.error(`Error fetching Paddle price for ${tier.name}:`, error)
              // On error, keep the price from API and mark as not loading
              setPricingTiers((prev) =>
                prev.map((t) =>
                  t.name === tier.name
                    ? { ...t, loading: false }
                    : t
                )
              )
            })
          }
        })
      } catch (error) {
        console.error('Error fetching Paddle prices:', error)
      }
    }

    fetchPaddlePrices()
  }, [paddleInitialized, loading, pricingTiers.length])

  const handleViewFullPricing = () => {
    navigate('/pricing')
  }

  return (
    <section id="pricing" className="section-spacing bg-panel/30 px-4 sm:px-6" aria-labelledby="pricing-heading">
      <div className="container-custom">
        <AnimatedSection animation="fade-up">
          <div className="text-center mb-8 sm:mb-12">
            <h2 id="pricing-heading" className="text-3xl sm:text-4xl font-bold text-text mb-3 sm:mb-4">
              Predictable pricing.{' '}
              <span className="text-gradient">Optimized credits.</span>
            </h2>
            <p className="text-base sm:text-lg text-text-muted max-w-2xl mx-auto px-4">
              You're not just buying credits - you're buying credits that go further. As we roll out optimization features, your existing plan gets more powerful automatically.
            </p>
          </div>
        </AnimatedSection>

        {loading ? (
          <div className="flex justify-center items-center py-12 sm:py-20" role="status" aria-live="polite">
            <div className="text-center">
              <div className="spinner h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-4" aria-hidden="true"></div>
              <p className="text-sm sm:text-base text-text-muted">Loading pricing plans...</p>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12">
              {pricingTiers.map((tier, index) => (
                <AnimatedSection
                  key={tier.name}
                  animation="scale-in"
                  delay={index * 100}
                >
                  <article
                    className={`card-enhanced p-5 sm:p-6 h-full ${
                      tier.popular
                        ? 'ring-2 ring-accent shadow-xl shadow-accent/20 relative'
                        : ''
                    }`}
                    aria-label={`${tier.name} pricing plan${tier.popular ? ' - Most popular' : ''}`}
                  >
                    {tier.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <div className="bg-gradient-to-r from-accent to-accent-2 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                          <Zap className="w-3 h-3" aria-hidden="true" />
                          POPULAR
                        </div>
                      </div>
                    )}

                    <div className="text-center mb-4 sm:mb-6">
                      <h3 className="text-lg sm:text-xl font-bold text-text mb-2">
                        {tier.name}
                      </h3>
                      <p className="text-text-muted text-xs sm:text-sm mb-3 sm:mb-4">
                        {tier.description}
                      </p>

                      <div className="mb-3 sm:mb-4">
                        {tier.loading ? (
                          <div className="flex justify-center">
                            <div className="spinner h-8 w-8" aria-label="Loading price"></div>
                          </div>
                        ) : (
                          <div className="flex items-baseline justify-center">
                            <span className="text-3xl sm:text-4xl font-extrabold text-gradient">
                              {tier.price.split('/')[0]}
                            </span>
                            {tier.price !== '$0' && (
                              <span className="text-xs sm:text-sm text-text-muted ml-1">
                                /mo
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6" aria-label={`${tier.name} plan features`}>
                      {tier.topFeatures.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" aria-hidden="true" />
                          <span className="text-text-muted text-xs sm:text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                </AnimatedSection>
              ))}
            </div>

            <AnimatedSection animation="fade-up" delay={400}>
              <div className="text-center">
                <button
                  onClick={handleViewFullPricing}
                  className="px-5 sm:px-6 py-3 bg-transparent hover:bg-accent/10 text-accent border border-accent/30 hover:border-accent/50 rounded-lg text-sm sm:text-base font-semibold transition-all duration-200 inline-flex items-center gap-2 min-h-[44px]"
                  aria-label="View full pricing details"
                >
                  View Full Pricing
                  <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                </button>
              </div>
            </AnimatedSection>
          </>
        )}
      </div>
    </section>
  )
})
